import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tab, Table, TableBody, TableCell, TableFooter, TableHead, TableRow } from '@material-ui/core';
import { BlueCell, BrightYellowCellBorder, CellNoBorder, DarkGreyCellBorder, GreenCell, LightGreyCell, OrangeCell, QtyInput, StyledTableRow, TableInput, TableSelect } from '../components/Page/TableStyles';
import {getFormData, setData} from '../store/slices/formDataSlice';
import { formatMoney, Phases, salesType, sunHours } from '../helpers';
import { SpacedDiv } from '../components/Page/FormStyles';

export const SiteBuilderPath = "/site-builder";

export default function SiteBuilder(props) {
    const dispatch = useDispatch();

    const formData = useSelector(getFormData);
    const allProducts = formData.allProducts;

    const updateFormData = (key, value) => {
        dispatch(setData({
            prop: key,
            value: value
        }));
    }

    //set the rails automatically as we only have 1
    useEffect(() => {
        if (!formData.railChosen && allProducts.loaded) {
            updateFormData('railChosen', allProducts.data.Rail[0])
        }
    }, [allProducts.loaded])

    //set up install dates for a few years ahead
    let years = [new Date().getFullYear()]
    for (var i = 1; i < 5; i++) {
        years.push(new Date().getFullYear() + i)
    }

    //get a list of available roof types, add an option for approval as well
    var roofTypes = [];
    if (allProducts?.data && allProducts.loaded) {
        allProducts.data.Racking.map(r => {
            if (roofTypes.indexOf(r.Type) === -1) {
                roofTypes.push(r.Type);
            }
        })
    }
    roofTypes.sort();
    roofTypes.push("Other (Notes and Approval Needed)")

    //update racking and rail qty if the panel or qty changes
    useEffect(() => {
        //if the system size has changed, re-work it out and set it
        if (formData.panelChosen) {
            //number of panels * the wattage / 1000
            updateFormData('siteBuildSystemSize', formData.panelChosenQty * formData.panelChosen.Wattage / 1000)
            //do the calculations for racking and rail here as well
            var kitsRaw = formData.panelChosenQty / 6;
            var kitsRound = Math.ceil(kitsRaw);
            var railRaw = kitsRound * 3.5;
            var railRound = Math.ceil(railRaw);
            updateFormData('railRoundedQty', railRound);
            updateFormData('rackingRoundedQty', kitsRound)
            //update the spbModule cost
            let val = Math.ceil((formData['panelChosen']?.PPP || 0) * formData['panelChosenQty']);
            updateFormData('spbModules', val)
        } else {
            updateFormData('siteBuildSystemSize', 0);
        }
    }, [formData.panelChosen, formData.panelChosenQty])

    //using the roof type, find the racking that should be used
    const racking = allProducts.loaded ? allProducts.data.Racking.find(r => {
        return r.Type === formData.roofType;
    }) : null;

    //select new racking if the roof type changes
    useEffect(() => {
        //if the roof type has changed, update the racking chosen
        if (racking) {
            updateFormData('rackingChosen', racking)
        }
    }, [formData.roofType])

    useEffect(() => {
        //find the installation data and set that to the formData
        var install = allProducts.loaded ? allProducts.data.Installation.find(i => {
            return i.SalesArea.toLowerCase() == formData.officeName.toLowerCase();
        }) : null;
        if (install) {
            updateFormData('installRatePerPanel', install.Rate)
        } else {
            updateFormData('installRatePerPanel', 100)
        }

        //also do this for STC data - multiplier and the rate
        var stc = allProducts.loaded ? allProducts.data.STC.find(s => {
            return s.SalesArea.toLowerCase() == formData.officeName.toLowerCase();
        }) : null
        if (stc) {
            //years till 2031 * multiplier * systemsize
            var years = 2031 - formData.installYear;
            var stc = years * formData.siteBuildSystemSize * stc.Multiplier;
            var lowered = Math.floor(stc);
            updateFormData('stcTotalQuantity', lowered)
            updateFormData('stcRawQty', stc)
        } else {
            //use some defaults here
            var years = 2031 - formData.installYear;
            var stc = years * formData.siteBuildSystemSize * 1.382;
            var lowered = Math.floor(stc);
            updateFormData('stcTotalQuantity', lowered)
            updateFormData('stcRawQty', stc)
        }

    }, [formData.officeName, allProducts.loaded, formData.siteBuildSystemSize, formData.installYear])

    //if the panel count changes, the panel chosen changes, the sale type changes, inverter qty changes
    //update the installation fee
    useEffect(() => {
        if (formData.saleType == "Solar") {
            var cost = formData.panelChosenQty * formData.installRatePerPanel
            updateFormData('spbInstallation',cost )
        } else {
            //anything else its 175 * number of inverters?
            var inverter1 = formData.inverter1Chosen ? formData.inverter1Qty : 0;
            var inverter2 = formData.inverter2Chosen ? formData.inverter2Qty : 0;
            var cost = 175 * (inverter1 + inverter2);
            updateFormData('spbInstallation',cost )
        }

    },[formData.panelChosenQty, formData.panelChosen, formData.inverter1Qty, formData.inverter2Qty,
        formData.inverter1Chosen, formData.inverter2Chosen, formData.saleType
    ])

    //update the values for the inverter if the inverter data changes
    useEffect(() => {
        //get the CostEx of the chosen 2 inverters, set the spbInverter value to that
        var inv1 = formData.inverter1Chosen?.CostEx || 0;
        var inv2 = formData.inverter2Chosen?.CostEx || 0;
        let val = Math.ceil((inv1 * formData.inverter1Qty) + (inv2 * formData.inverter2Qty));
        updateFormData('spbInverter', val)
    }, [formData.inverter1Chosen, formData.inverter2Chosen, formData.inverter1Qty, formData.inverter2Qty])

    //update spbRacking when rails or racking change
    useEffect(() => {
        var railPrice = Math.ceil((formData['railChosen']?.Cost || 0) * formData.railRoundedQty);
        var rackingPrice = Math.ceil((formData.rackingChosen?.Cost || 0) * formData.rackingRoundedQty);
        let val = railPrice + rackingPrice;
        updateFormData('spbRacking', val)
    }, [formData.railChosen, formData.rackingChosen, formData.railRoundedQty, formData.rackingRoundedQty])

    //update total inc and total ex for cost
    useEffect(() => {
        var ex = formData.spbRacking + formData.spbModules + formData.spbInverter + formData.spbInstallation;
        updateFormData('spbTotalEx', ex);
        updateFormData('spbTotalInc', ex * 1.1);
    }, [formData.spbRacking, formData.spbModules, formData.spbInverter, formData.spbInstallation])

    //update stc data
    useEffect(() => {
        //find the stc from the products using the sales area
        //multiply the stcTotalQuantity by the multiplier
        var stc = formData.allProducts.loaded ? formData.allProducts.data.STC.find(s => {
            return s.SalesArea.toLowerCase() == formData['officeName'].toLowerCase();
        }) : null
        if (stc) {
            updateFormData('spbSTC', stc.Rate * formData.stcTotalQuantity)
        }
    }, [formData.allProducts.loaded, formData.stcRawQty, formData.stcTotalQuantity])

    //update the true cost
    useEffect(() => {
        let val = formData.spbSTC + formData.spbTotalInc;
        updateFormData('spbTrueCost', val)
    }, [formData.spbSTC, formData.spbTotalInc])

    //update the overheads as required
    useEffect(() => {
        if (allProducts.loaded) {
            //marketing spb
            var marketing = allProducts.data.Overheads.find(o => {
                return o.Type === "Marketing"
            })
            if (marketing) {
                updateFormData('spbMarketing', marketing.Value)
            }
            //cc spb
            var cc = allProducts.data.Overheads.find(o => {
                return o.Type === "Call Centre"
            })
            if (cc) {
                //if it is solar, multiple kwh * value, otherwise it is 100
                var val;
                if (formData.saleType !== "Solar") {
                    val = 100;
                } else {
                    val = cc.Value * formData.siteBuildSystemSize
                }
                updateFormData('spbCallCentre', val)
            }
            //delivery & m spb
            var delivery = allProducts.data.Overheads.find(o => {
                return o.Type === "Delivery & M"
            })
            if (delivery) {
                // spbDeliveryAndM
                var val = delivery.Value * formData.siteBuildSystemSize
                updateFormData('spbDeliveryAndM', val)
            }
            //dd spb
            var dd = allProducts.data.Overheads.find(o => {
                return o.Type === "DD"
            })
            if (dd) {
                var val = dd.Value * formData.panelChosenQty;
                updateFormData('spbDD', val)
            }
        }
    }, [formData.allProducts, formData.saleType, formData.siteBuildSystemSize, formData.panelChosenQty])

    //update the total overheads
    useEffect(() => {
        var val = formData.spbMarketing + formData.spbCallCentre + formData.spbDeliveryAndM + formData.spbDD;
        updateFormData('spbOverheadTotal', val)
    }, [formData.spbMarketing, formData.spbCallCentre, formData.spbDeliveryAndM, formData.spbDD])

    //update the floor cost
    useEffect(() => {
        var val = formData.spbOverheadTotal + formData.spbTrueCost;
        updateFormData('spbFloorCost', val);
    }, [formData.spbOverheadTotal, formData.spbTrueCost])

    //update the lcode
    useEffect(() => {
        let perc = formData.saleType === "Solar" ? 0.05 : 0;
        let lcode = formData.spbFloorCost / (1 - perc);
        updateFormData('spbLCode', lcode)
    }, [formData.spbFloorCost, formData.saleType])

    //update the show home value
    useEffect(() => {
        let perc = formData.saleType === "Solar" ? 0.225 : 0.001;
        var showHome = formData.spbLCode / (1 - perc);
        updateFormData('spbShowHome', showHome)
    }, [formData.spbLCode, formData.saleType])

    //update the hcode
    useEffect(() => {
        let add = formData.saleType === "Solar" ? 1000 : 0;
        updateFormData('spbHCode', formData.spbShowHome + add)
    }, [formData.saleType, formData.spbShowHome])

    return (
        <div style={{padding: '10px 30px'}}>
            <div style={{maxWidth: '350px'}}>
                <Table size="small">
                    <TableBody>
                        <StyledTableRow>
                            <DarkGreyCellBorder colSpan={2} style={{textAlign: 'center'}}>Site Details</DarkGreyCellBorder>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell style={{width: "50%"}}>Sales Area</TableCell>
                            <GreenCell>
                                <TableSelect value={formData.officeName || ''} onChange={(e) => updateFormData('officeName', e.target.value)} style={{width: '100%'}}>
                                    {
                                        Object.keys(sunHours).map(k => {
                                            return <option value={k} key={`office-${k}`}>{k}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>Sale Type</TableCell>
                            <GreenCell>
                                <TableSelect value={formData.saleType || ''} onChange={(e) => updateFormData('saleType', e.target.value)} style={{width: '100%'}}>
                                    {
                                        salesType.map((k) => {
                                            return <option value={k} key={`sale-type-${k}`}>{k}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>Install Year</TableCell>
                            <GreenCell>
                                <TableSelect value={formData.installYear} onChange={(e) => updateFormData('installYear', e.target.value)} style={{width: '100%'}}>
                                    {
                                        years.map((k) => {
                                            return <option value={k} key={`install-year-${k}`}>{k}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>Phases</TableCell>
                            <GreenCell>
                                <TableSelect value={formData.phases || ''} onChange={(e) => updateFormData('phases', e.target.value)} style={{width: '100%'}}>
                                    {
                                        Phases.map((k) => {
                                            return <option value={k} key={`phases-${k}`}>{k}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>Roof Type</TableCell>
                            <GreenCell>
                                <TableSelect value={formData.roofType || ''} onChange={(e) => updateFormData('roofType', e.target.value)} style={{width: '100%'}}>
                                    <option value=""></option>
                                    {
                                        roofTypes.map((k) => {
                                            return <option value={k} key={`roof-type-${k}`}>{k}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>System Size</TableCell>
                            <TableCell><TableInput disabled={true} value={formData.siteBuildSystemSize} /></TableCell>
                        </StyledTableRow>
                    </TableBody>
                </Table>
            </div>
            <SpacedDiv>
                <Table size="small">
                    <TableHead>
                        <StyledTableRow>
                            <BrightYellowCellBorder>Inverter Options</BrightYellowCellBorder>
                            <BrightYellowCellBorder>SKU</BrightYellowCellBorder>
                            <BrightYellowCellBorder>Description</BrightYellowCellBorder>
                            <BrightYellowCellBorder>PD-ID</BrightYellowCellBorder>
                            <BrightYellowCellBorder>QTY</BrightYellowCellBorder>
                        </StyledTableRow>
                    </TableHead>
                    <TableBody>
                        <StyledTableRow>
                            <TableCell>Inverter 1 Chosen</TableCell>
                            <GreenCell>
                                <TableSelect value={JSON.stringify(formData.inverter1Chosen) || '[]'} onChange={(e) => updateFormData('inverter1Chosen', e.target.value)} style={{width: '100%'}}>
                                    <option value="[]"></option>
                                    {
                                        allProducts.loaded && allProducts.data.Inverters.map((k, v) => {
                                            return <option value={JSON.stringify(k)} key={`inverter1-chosen-${k.PipedriveID}`}>{k.SKU}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                            <TableCell>{formData.inverter1Chosen && formData.inverter1Chosen.Description}</TableCell>
                            <TableCell>{formData.inverter1Chosen && formData.inverter1Chosen.PipedriveID}</TableCell>
                            <GreenCell>
                                <QtyInput
                                    value={formData.inverter1Qty.toString()}
                                    onChange={(e) => updateFormData('inverter1Qty', parseInt(e.target.value) || 0)}
                                />
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>Inverter 2 Chosen</TableCell>
                            <GreenCell>
                                <TableSelect value={JSON.stringify(formData.inverter2Chosen) || '[]'} onChange={(e) => updateFormData('inverter2Chosen', e.target.value)} style={{width: '100%'}}>
                                    <option value="[]"></option>
                                    {
                                        allProducts.loaded && allProducts.data.Inverters.map((k, v) => {
                                            return <option value={JSON.stringify(k)} key={`inverter2-chosen-${k.PipedriveID}`}>{k.SKU}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                            <TableCell>{formData.inverter2Chosen && formData.inverter2Chosen.Description}</TableCell>
                            <TableCell>{formData.inverter2Chosen && formData.inverter2Chosen.PipedriveID}</TableCell>
                            <GreenCell>
                                <QtyInput
                                    value={formData.inverter2Qty.toString()}
                                    onChange={(e) => updateFormData('inverter2Qty', parseInt(e.target.value) || 0)}
                                />
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <CellNoBorder>&nbsp;</CellNoBorder>
                        </StyledTableRow>
                        <StyledTableRow>
                            <BrightYellowCellBorder>Panel Chosen</BrightYellowCellBorder>
                            <GreenCell>
                                <TableSelect value={JSON.stringify(formData.panelChosen) || '[]'} onChange={(e) => updateFormData('panelChosen', e.target.value)} style={{width: '100%'}}>
                                    <option value="[]"></option>
                                    {
                                        allProducts.loaded && allProducts.data.Panels.map((k, v) => {
                                            return <option value={JSON.stringify(k)} key={`panel-chosen-${k.PipedriveID}`}>{k.SKU}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                            <TableCell>{formData.panelChosen && formData.panelChosen.Description}</TableCell>
                            <TableCell>{formData.panelChosen && formData.panelChosen.PipedriveID}</TableCell>
                            <GreenCell>
                                <QtyInput
                                    value={formData.panelChosenQty.toString()}
                                    onChange={(e) => updateFormData('panelChosenQty', parseInt(e.target.value) || 0)}
                                />
                            </GreenCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <CellNoBorder>&nbsp;</CellNoBorder>
                        </StyledTableRow>
                        <StyledTableRow>
                            <BrightYellowCellBorder>Racking Chosen</BrightYellowCellBorder>
                            <LightGreyCell>{formData.rackingChosen && formData.rackingChosen.SKU}</LightGreyCell>
                            <TableCell>{formData.rackingChosen && formData.rackingChosen.Description}</TableCell>
                            <TableCell>{formData.rackingChosen && formData.rackingChosen.PipedriveID}</TableCell>
                            <TableCell>{formData.rackingRoundedQty}</TableCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <BrightYellowCellBorder>Rail Chosen</BrightYellowCellBorder>
                            <LightGreyCell>{formData.railChosen && formData.railChosen.SKU}</LightGreyCell>
                            <TableCell>{formData.railChosen && formData.railChosen.Description}</TableCell>
                            <TableCell>{formData.railChosen && formData.railChosen.PipedriveID}</TableCell>
                            <TableCell>{formData.railRoundedQty}</TableCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <CellNoBorder>&nbsp;</CellNoBorder>
                        </StyledTableRow>
                        <StyledTableRow>
                            <BrightYellowCellBorder>Installation</BrightYellowCellBorder>
                            <LightGreyCell>{allProducts.loaded && allProducts.data.Installation[0].SKU}</LightGreyCell>
                            <TableCell>{allProducts.loaded && allProducts.data.Installation[0].Description}</TableCell>
                            <TableCell>{allProducts.loaded && allProducts.data.Installation[0].PipedriveID}</TableCell>
                            <TableCell>{formData.panelChosenQty}</TableCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <BrightYellowCellBorder>STC</BrightYellowCellBorder>
                            <LightGreyCell>{allProducts.loaded && allProducts.data.STC[0].SKU}</LightGreyCell>
                            <TableCell>{allProducts.loaded && allProducts.data.STC[0].Description}</TableCell>
                            <TableCell>{allProducts.loaded && allProducts.data.STC[0].PipedriveID}</TableCell>
                            <TableCell>{formData.stcTotalQuantity}</TableCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <CellNoBorder>&nbsp;</CellNoBorder>
                        </StyledTableRow>
                        <StyledTableRow>
                            <OrangeCell colSpan={3}>Monitoring Information</OrangeCell>
                            <OrangeCell>PD-ID</OrangeCell>
                            <OrangeCell>QTY</OrangeCell>
                            <OrangeCell>Price</OrangeCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>Hardware Chose</TableCell>
                            <GreenCell>
                                <TableSelect value={JSON.stringify(formData.monitoringChosen) || '[]'} onChange={(e) => updateFormData('monitoringChosen', e.target.value)} style={{width: '100%'}}>
                                    <option value="[]"></option>
                                    {
                                        allProducts.loaded && allProducts.data.Monitoring.map((k, v) => {
                                            return <option value={JSON.stringify(k)} key={`monitoring-chosen-${k.PipedriveID}`}>{k.SKU}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                            <TableCell>{formData.monitoringChosen && formData.monitoringChosen.Description}</TableCell>
                            <TableCell>{formData.monitoringChosen && formData.monitoringChosen.PipedriveID}</TableCell>
                            <GreenCell>
                                <QtyInput
                                    value={formData.monitoringChosenQty.toString()}
                                    onChange={(e) => updateFormData('monitoringChosenQty', parseInt(e.target.value) || 0)}
                                />
                            </GreenCell>
                            <TableCell>{formData.monitoringChosen && formatMoney(formData.monitoringChosen?.Price || 0)}</TableCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell>Connection Type</TableCell>
                            <GreenCell>
                                <TableSelect value={JSON.stringify(formData.monitoringConnectionChosen) || '[]'} onChange={(e) => updateFormData('monitoringConnectionChosen', e.target.value)} style={{width: '100%'}}>
                                    <option value="[]"></option>
                                    {
                                        allProducts.loaded && allProducts.data.Connections.map((k, v) => {
                                            return <option value={JSON.stringify(k)} key={`connection-chosen-${k.PipedriveID}`}>{k.SKU}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                            <TableCell>{formData.monitoringConnectionChosen && formData.monitoringConnectionChosen.Description}</TableCell>
                            <TableCell>{formData.monitoringConnectionChosen && formData.monitoringConnectionChosen.PipedriveID}</TableCell>
                            <GreenCell>
                                <QtyInput
                                    value={formData.monitoringConnectionChosenQty.toString()}
                                    onChange={(e) => updateFormData('monitoringConnectionChosenQty', parseInt(e.target.value) || 0)}
                                />
                            </GreenCell>
                            <TableCell>{formData.monitoringConnectionChosen && formatMoney(formData.monitoringConnectionChosen?.Price || 0)}</TableCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <CellNoBorder>&nbsp;</CellNoBorder>
                        </StyledTableRow>
                        <StyledTableRow>
                            <BlueCell colSpan={3}>Battery / Storage Information</BlueCell>
                            <BlueCell>PD-ID</BlueCell>
                            <BlueCell>QTY</BlueCell>
                            <BlueCell>Price</BlueCell>
                        </StyledTableRow>
                        <StyledTableRow>
                            <TableCell style={{width: '15%'}}>Battery Chosen Chose</TableCell>
                            <GreenCell style={{width: '15%'}}>
                                <TableSelect value={JSON.stringify(formData.batteryChosen) || '[]'} onChange={(e) => updateFormData('batteryChosen', e.target.value)} style={{width: '100%'}}>
                                    <option value="[]"></option>
                                    {
                                        allProducts.loaded && allProducts.data.Batteries.map((k, v) => {
                                            return <option value={JSON.stringify(k)} key={`battery-chosen-${k.PipedriveID}`}>{k.SKU}</option>
                                        })
                                    }
                                </TableSelect>
                            </GreenCell>
                            <TableCell style={{width: '50%'}}>{formData.batteryChosen && formData.batteryChosen.Description}</TableCell>
                            <TableCell style={{width: '7%'}}>{formData.batteryChosen && formData.batteryChosen.PipedriveID}</TableCell>
                            <GreenCell style={{width: '50px'}}>
                                <QtyInput
                                    value={formData.batteryChosenQty.toString()}
                                    onChange={(e) => updateFormData('batteryChosenQty', parseInt(e.target.value) || 0)}
                                />
                            </GreenCell>
                            <TableCell style={{width: '10%'}}>{formData.batteryChosen && formatMoney(formData.batteryChosen.RRP || 0)}</TableCell>
                        </StyledTableRow>
                    </TableBody>
                </Table>
            </SpacedDiv>
        </div>
    )
}